import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  Calender,
  CloseCircle,
  Clock,
  Location,
  User,
  Users,
} from "../../Theme/Icons";
import { PopupBody, PopupWrapper } from "./CommonPopup";
import { formatActivityTime } from "./BookingDetailsPopup";
import { formatBookingTime, bookingDurationMinutes } from "../../../utils/util";
import {
  Badge,
  Breaker,
  Button,
  Flexbox,
  InputField,
  WrapperBox,
} from "../../Theme/GlobalStyles";
import { Text } from "../../Theme/Typography";
import { globalTheme } from "../../Theme/Theme";
import {
  classroomData,
  getRequestComments,
  addRequestComment,
  clearRequestComments,
} from "../../../redux/classroom/classroomSlice";

// Resolve a participant's display name across the two attendee shapes:
// cowork bookings nest CoworkUser; classroom/lab requests carry name/label.
const attendeeName = (a) =>
  `${a?.CoworkUser?.firstName || ""} ${a?.CoworkUser?.lastName || ""}`.trim() ||
  a?.name ||
  a?.label ||
  `${a?.firstName || ""} ${a?.lastName || ""}`.trim() ||
  "Guest";

const initialsFor = (firstName = "", lastName = "") => {
  const a = (firstName || "").trim()[0] || "";
  const b = (lastName || "").trim()[0] || "";
  return (a + b).toUpperCase() || "—";
};

const statusBadgeProps = (status) => {
  if (status === "Booked") return { blue: true };
  if (status === "Approved") return { green: true };
  if (status === "Rejected" || status === "Cancelled") return { red: true };
  return { yellow: true };
};

// One cell in the booking-details grid: an icon chip + a label / value stack.
const InfoItem = ({ icon, label, value }) => (
  <Flexbox alignStart gap={10} noWrap style={{ width: "100%" }}>
    <Flexbox
      alignCenter
      justiCenter
      maxWidth="max-content"
      style={{
        width: 34,
        height: 34,
        borderRadius: 10,
        background: globalTheme.colors.grey8,
        flexShrink: 0,
      }}
    >
      {icon}
    </Flexbox>
    <Flexbox column gap={2} alignStart style={{ minWidth: 0 }}>
      <Text font11 inter regular nuetral6>
        {label}
      </Text>
      <Text
        font13
        inter
        semiBold
        black3
        style={{ wordBreak: "break-word" }}
      >
        {value}
      </Text>
    </Flexbox>
  </Flexbox>
);

const CommentRow = ({ comment }) => {
  const isActivity =
    comment?.type === "activity" || (!comment?.comment && !!comment?.activity);
  const c = comment?.commenter || {};
  const name = isActivity
    ? "Activity"
    : `${c.firstName || ""} ${c.lastName || ""}`.trim() || "User";
  const body = isActivity ? comment?.activity : comment?.comment;
  return (
    <Flexbox gap={10} alignStart noWrap>
      <Flexbox
        alignCenter
        justiCenter
        maxWidth="max-content"
        style={{
          width: 34,
          height: 34,
          minWidth: 34,
          borderRadius: "50%",
          background: globalTheme.colors.grey8,
          border: `1px solid ${globalTheme.colors.nuetral2}`,
          flexShrink: 0,
        }}
      >
        <Text font11 inter semiBold nuetral8 style={{ lineHeight: 1 }}>
          {isActivity ? "•" : initialsFor(c.firstName, c.lastName)}
        </Text>
      </Flexbox>
      <Flexbox column gap={2} alignStart style={{ flex: 1, minWidth: 0 }}>
        <Flexbox gap={8} alignCenter>
          <Text font13 inter semiBold black3>
            {name}
          </Text>
          <Text font11 inter regular nuetral6 style={{ whiteSpace: "nowrap" }}>
            {formatActivityTime(comment?.created_at)}
          </Text>
        </Flexbox>
        <Text
          font13
          inter
          regular
          grey2
          style={{
            wordBreak: "break-word",
            fontStyle: isActivity ? "italic" : "normal",
          }}
        >
          {body}
        </Text>
      </Flexbox>
    </Flexbox>
  );
};

// Booking preview: read-only booking details on top, threaded comments below.
// Comments reuse the request-comment thunks (Makers Lab / Eduspace requests).
// The backend emails the relevant people when a comment is posted — see the
// "Comment Notifications" section in CLASSROOM_BOOKING_README.md.
export const BookingPreviewBody = ({ request, onClose }) => {
  const dispatch = useDispatch();
  const {
    requestCommentsList,
    isCommentsFetching,
    isPostingComment,
    commentPostSuccess,
  } = useSelector(classroomData);

  const [commentText, setCommentText] = useState("");

  const requestId = request?.id;

  // Load this request's comments when the popup opens for it.
  useEffect(() => {
    if (!requestId) return;
    dispatch(clearRequestComments());
    dispatch(getRequestComments({ requestId }));
    return () => {
      dispatch(clearRequestComments());
    };
  }, [dispatch, requestId]);

  // Clear the input once a post succeeds.
  useEffect(() => {
    if (commentPostSuccess) setCommentText("");
  }, [commentPostSuccess]);

  if (!request) return null;

  const user = request.requestingUser || request.CoworkUser || {};
  const bookedBy =
    `${user.firstName || ""} ${user.lastName || ""}`.trim() || "—";
  const room = request.room || {};
  const attendees = request.attendees || [];
  const dateStr = moment.utc(request.start_time).format("ddd, MMM DD, YYYY");
  // Both come from the request's 30-minute slot rows, so a booking of
  // non-adjacent slots reads as two ranges and its duration excludes the gap —
  // start_time/end_time alone would report the whole span as booked.
  const timeStr = formatBookingTime(request);
  const durationMins = bookingDurationMinutes(request);
  const comments = requestCommentsList || [];

  const handlePost = () => {
    const text = commentText.trim();
    if (!text || !requestId) return;
    dispatch(addRequestComment({ requestId, comment: text }));
  };

  return (
    <>
      <Flexbox spaceBW alignCenter>
        <Text font18 inter semiBold white>
          Booking Preview
        </Text>
        <CloseCircle className="pointer" onClick={() => onClose?.()} />
      </Flexbox>

      <Flexbox
        style={{
          height: 1,
          background: globalTheme.colors.white1,
          opacity: 0.18,
          margin: "14px 0 18px",
        }}
      />

      {/* ===== Details + comments, side by side ===== */}
      <Flexbox gap={14} wrap alignStart style={{ alignItems: "stretch" }}>
        {/* ===== Booking details ===== */}
        <WrapperBox
          background={globalTheme.colors.white1}
          borderRadius={16}
          padding={"20px"}
          overflow={"hidden"}
          style={{ flex: 1, minWidth: 300 }}
        >
        {/* Room heading + status */}
        <Flexbox spaceBW alignStart noWrap gap={12}>
          <Flexbox column gap={4} alignStart style={{ minWidth: 0 }}>
            <Text
              font18
              inter
              semiBold
              black3
              style={{ wordBreak: "break-word" }}
            >
              {room?.room_name || "Room"}
            </Text>
            <Flexbox alignCenter gap={6} maxWidth="max-content">
              <Location color={globalTheme.colors.nuetral7} />
              <Text font12 inter regular nuetral8>
                {room?.tower ? `Tower ${room.tower}` : "—"}
              </Text>
            </Flexbox>
          </Flexbox>
          <Flexbox column gap={6} alignEnd maxWidth="max-content">
            <Badge
              statusText={request.status}
              {...statusBadgeProps(request.status)}
              borderRadius={"20px"}
              small
            />
            <Text font12 inter regular nuetral6>
              #{request?.ticket_id || request?.id || "—"}
            </Text>
          </Flexbox>
        </Flexbox>

        <Breaker marginTop={16} marginBottom={16} />

        {/* Info grid */}
        <Flexbox wrap gap={16} alignStart>
          <InfoItem
            icon={<Calender color={globalTheme.colors.nuetral7} />}
            label="Date"
            value={dateStr}
          />
          <InfoItem
            icon={<Clock color={globalTheme.colors.nuetral7} />}
            label="Time"
            value={
              Number.isFinite(durationMins)
                ? `${timeStr} · ${durationMins} mins`
                : timeStr
            }
          />
          <InfoItem
            icon={<User color={globalTheme.colors.nuetral7} />}
            label="Booked by"
            value={bookedBy}
          />
          <InfoItem
            icon={
              <Users
                color={globalTheme.colors.nuetral7}
                width="16px"
                height="16px"
              />
            }
            label="Attendees"
            value={String(attendees.length).padStart(2, "0")}
          />
          {/* Bloomberg books per seat, so how many seats the request takes is a
              distinct fact from who was invited — only shown when it's set. */}
          {request.isBmb && (
            <InfoItem
              icon={
                <Users
                  color={globalTheme.colors.nuetral7}
                  width="16px"
                  height="16px"
                />
              }
              label="Seats booked"
              value={`${Math.max(1, Number(request.participants) || 1)}${
                request.room?.capacity ? ` of ${request.room.capacity}` : ""
              }`}
            />
          )}
          {request.meeting_title && (
            <InfoItem
              icon={<Calender color={globalTheme.colors.nuetral7} />}
              label="Meeting"
              value={request.meeting_title}
            />
          )}
        </Flexbox>

        {request.reason_for_rejection && (
          <Flexbox
            column
            gap={4}
            alignStart
            padding={"10px 12px"}
            background={globalTheme.colors.primary1}
            borderRadius={10}
            style={{ marginTop: 16 }}
          >
            <Text font11 inter regular nuetral6>
              Rejection reason
            </Text>
            <Text font13 inter regular black3>
              {request.reason_for_rejection}
            </Text>
          </Flexbox>
        )}

        {attendees.length > 0 && (
          <>
            <Breaker marginTop={16} marginBottom={14} />
            <Flexbox column gap={10} alignStart>
              <Text font12 inter medium nuetral6>
                Participants
              </Text>
              <Flexbox wrap gap={8}>
                {attendees.map((a, i) => (
                  <Badge
                    key={i}
                    blue
                    small
                    borderRadius={"20px"}
                    statusText={attendeeName(a)}
                  />
                ))}
              </Flexbox>
            </Flexbox>
          </>
        )}
      </WrapperBox>

        {/* ===== Comments ===== */}
        <WrapperBox
          background={globalTheme.colors.white1}
          borderRadius={16}
          padding={"20px"}
          overflow={"hidden"}
          style={{
            flex: 1,
            minWidth: 300,
            display: "flex",
            flexDirection: "column",
          }}
        >
        <Flexbox spaceBW alignCenter>
          <Flexbox alignCenter gap={8} maxWidth="max-content">
            <Text font16 inter semiBold black3>
              Comments
            </Text>
            <Flexbox
              alignCenter
              justiCenter
              maxWidth="max-content"
              background={globalTheme.colors.grey8}
              borderRadius={50}
              style={{ minWidth: 22, height: 20, padding: "0 7px" }}
            >
              <Text font11 inter semiBold nuetral8>
                {comments.length}
              </Text>
            </Flexbox>
          </Flexbox>
        </Flexbox>

        <Breaker marginTop={14} marginBottom={14} />

        <Flexbox
          column
          gap={16}
          alignStart
          style={{ flex: 1, minHeight: 90, maxHeight: 360, overflowY: "auto" }}
        >
          {isCommentsFetching ? (
            <Flexbox
              justiCenter
              alignCenter
              style={{ width: "100%", padding: "16px 0" }}
            >
              <Text font13 inter regular grey2>
                Loading comments…
              </Text>
            </Flexbox>
          ) : comments.length > 0 ? (
            comments.map((c, i) => <CommentRow key={c.id || i} comment={c} />)
          ) : (
            <Flexbox
              column
              gap={4}
              justiCenter
              alignCenter
              style={{ width: "100%", padding: "14px 0" }}
            >
              <Text font13 inter medium black3>
                No comments yet
              </Text>
              <Text font12 inter regular nuetral6>
                Be the first to start the conversation.
              </Text>
            </Flexbox>
          )}
        </Flexbox>

        <Breaker marginTop={14} marginBottom={14} />

        <Flexbox column gap={12} alignStart>
          <InputField
            noLabel
            type="textarea"
            rows={2}
            placeholder="Write a comment…"
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
          />
          <Flexbox flexEnd>
            <Button
              buttonMd
              primaryFilled
              primary
              maxWidth="max-content"
              name={isPostingComment ? "Posting…" : "Post Comment"}
              disabled={isPostingComment || !commentText.trim()}
              onClick={handlePost}
            />
          </Flexbox>
        </Flexbox>
        </WrapperBox>
      </Flexbox>
    </>
  );
};

const BookingPreviewPopup = ({ showActive, request, onClose }) => (
  <PopupWrapper className={`${showActive ? "active" : ""}`}>
    <PopupBody maxWidth={920} maxHeight={620} padding={24}>
      {showActive && <BookingPreviewBody request={request} onClose={onClose} />}
    </PopupBody>
  </PopupWrapper>
);

export default BookingPreviewPopup;
